/**
 * Settings: run the hourly tick's passes by hand (plan docs/plans/lms-plan.md
 * 9.1, 9.3, 14.1 item 7, Appendix A "Settings and jobs"; 18.26, 18.27;
 * WP-48). POST /admin/jobs/run-daily and run-weekly run the SAME claimed pass
 * the 07:05 tick runs, once per local date (daily) or ISO week (weekly) for
 * the network, whoever runs it first:
 *
 * - claimed: true  -> the pass ran now; its job row's status (done or failed,
 *   G10) and its counts are shown, the failed steps named first;
 * - claimed: false -> it already ran for this period (tick_key); the newest
 *   job row of that kind (last_job) is shown instead, so the admin sees what
 *   already happened rather than a silent nothing.
 *
 * Each run asks for confirmation first (it writes reminders and, for the
 * daily pass, may send pending email when this hour's send is free), and the
 * outcome is toasted and kept on screen. onRan lets the host refresh the
 * health line (the last-run pills).
 */
import { useState } from 'react';
import { CalendarClock, Play } from 'lucide-react';
import ConfirmModal from '../../../../ui/ConfirmModal';
import { secondaryBtnCls } from '../../../../ui/styles';
import { useToast } from '../../../../ui/Toast';
import { apiErrorMessage } from '../../../api/client';
import { runJobNow } from '../../../api/settings';
import type { LmsJobKind, LmsJobRunResult } from '../../../api/settings';
import { cardCls, ErrorNote, metaCls } from '../../../components/pickers/shared';
import { cardTitleCls, formatInstant, JobStatusPill, Pill, summaryLines } from './shared';

const KIND_TEXT: Record<LmsJobKind, { name: string; button: string; confirm: string; period: string }> = {
  daily: {
    name: 'The daily pass',
    button: 'Run the daily pass',
    confirm:
      "It abandons expired attempts, reconciles profiles, creates renewals, writes due, overdue and expiry reminders, retries certificates and sends pending email. It runs once per local date for the network: if today's pass already ran, nothing runs again.",
    period: 'today',
  },
  weekly: {
    name: 'The weekly digest',
    button: 'Run the weekly digest',
    confirm:
      "It builds the admin digest of overdue, expiring and waiting items for every Learning Center admin. It runs once per week for the network: if this week's digest already went out, nothing runs again. The digest emails leave with the next hourly send.",
    period: 'this week',
  },
};

interface Outcome {
  kind: LmsJobKind;
  result: LmsJobRunResult;
}

export interface RunNowPanelProps {
  onRan?: () => void;
}

function OutcomeView({ outcome }: { outcome: Outcome }) {
  const { kind, result } = outcome;
  const text = KIND_TEXT[kind];
  if (result.claimed) {
    const failed = result.status === 'failed';
    const lines = summaryLines(result.summary);
    return (
      <div role="status" data-testid="lms-run-result" data-kind={kind} data-claimed="true" className="space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-900 dark:text-gray-100">
          <JobStatusPill status={result.status ?? 'done'} testId="lms-run-result-pill" />
          <span>{failed ? `${text.name} ran and recorded a failure.` : `${text.name} ran now.`}</span>
        </div>
        {lines.length > 0 ? (
          <ul className="grid gap-x-4 gap-y-0.5 sm:grid-cols-2" data-testid="lms-run-result-lines">
            {lines.map((line) => (
              <li key={line} className={metaCls}>
                {line}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }
  const last = result.last_job ?? null;
  const when = formatInstant(last?.finished_at ?? last?.started_at ?? null);
  return (
    <div role="status" data-testid="lms-run-result" data-kind={kind} data-claimed="false" className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-900 dark:text-gray-100">
        <Pill tone="gray" testId="lms-run-result-pill">
          Already ran
        </Pill>
        <span>{`${text.name} already ran ${text.period}, so nothing ran again.`}</span>
      </div>
      {last ? (
        <p className={`${metaCls} flex flex-wrap items-center gap-1.5`}>
          <span>Last run:</span>
          <JobStatusPill status={last.status} />
          {when ? <span>{when}</span> : null}
        </p>
      ) : (
        <p className={metaCls}>Another run holds this period; the status line shows the last one.</p>
      )}
    </div>
  );
}

export function RunNowPanel({ onRan }: RunNowPanelProps) {
  const { toast } = useToast();
  const [confirm, setConfirm] = useState<LmsJobKind | null>(null);
  const [running, setRunning] = useState<LmsJobKind | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (kind: LmsJobKind) => {
    setRunning(kind);
    setError(null);
    try {
      const result = await runJobNow(kind);
      setOutcome({ kind, result });
      const name = KIND_TEXT[kind].name;
      if (!result.claimed) toast(`${name} already ran ${KIND_TEXT[kind].period}.`, { type: 'info' });
      else if (result.status === 'failed') toast(`${name} ran and recorded a failure.`, { type: 'error' });
      else toast(`${name} ran now.`);
      onRan?.();
    } catch (err) {
      const message = apiErrorMessage(err, `${KIND_TEXT[kind].name} could not be run.`);
      setError(message);
      toast(message, { type: 'error' });
    } finally {
      setRunning(null);
      setConfirm(null);
    }
  };

  return (
    <section className={cardCls} aria-labelledby="lms-settings-run-title" data-testid="lms-settings-run">
      <h2 id="lms-settings-run-title" className={cardTitleCls}>
        Run the tick by hand
      </h2>
      <p className={`${metaCls} mt-1`}>
        The hourly tick runs these on its own at the digest hour. Running one here takes the same once-per-period slot.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={secondaryBtnCls}
          onClick={() => setConfirm('daily')}
          disabled={running !== null}
          data-testid="lms-run-daily"
        >
          <Play className="h-4 w-4" aria-hidden="true" />
          {running === 'daily' ? 'Running...' : KIND_TEXT.daily.button}
        </button>
        <button
          type="button"
          className={secondaryBtnCls}
          onClick={() => setConfirm('weekly')}
          disabled={running !== null}
          data-testid="lms-run-weekly"
        >
          <CalendarClock className="h-4 w-4" aria-hidden="true" />
          {running === 'weekly' ? 'Running...' : KIND_TEXT.weekly.button}
        </button>
      </div>

      {error ? (
        <div className="mt-3">
          <ErrorNote testId="lms-run-error">{error}</ErrorNote>
        </div>
      ) : null}
      {outcome ? (
        <div className="mt-3 border-t border-gray-100 pt-3 dark:border-gray-700">
          <OutcomeView outcome={outcome} />
        </div>
      ) : null}

      <ConfirmModal
        isOpen={confirm !== null}
        onClose={() => {
          if (running === null) setConfirm(null);
        }}
        onConfirm={() => {
          if (confirm) void run(confirm);
        }}
        title={confirm ? `${KIND_TEXT[confirm].button}?` : ''}
        message={confirm ? KIND_TEXT[confirm].confirm : undefined}
        confirmLabel="Run now"
        loading={running !== null}
      />
    </section>
  );
}

export default RunNowPanel;
